import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen, handleClickOutside]);

  const closeAll = () => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const isTransparent = !scrolled && !mobileMenuOpen;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group cursor-pointer" onClick={closeAll}>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isTransparent ? 'bg-background-50/30' : 'bg-primary-500'}`}>
            <i className={`ri-file-list-3-line text-lg ${isTransparent ? 'text-background-50' : 'text-background-50'}`}></i>
          </div>
          <span className={`text-lg font-semibold font-heading tracking-tight transition-colors ${isTransparent ? 'text-background-50' : 'text-foreground-900'}`}>
            pandabuy<span className={isTransparent ? 'text-background-50/80' : 'text-primary-500'}>.living</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-4">
          <Link
            to="/"
            onClick={closeAll}
            className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isTransparent ? 'text-background-50/80 hover:text-background-50' : 'text-foreground-700 hover:text-foreground-900'}`}
          >
            Home
          </Link>

          <Link
            to="/pandabuy-spreadsheet-guide"
            onClick={closeAll}
            className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isTransparent ? 'text-background-50/80 hover:text-background-50' : 'text-foreground-700 hover:text-foreground-900'}`}
          >
            Shopping Guide
          </Link>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border ${
                isTransparent
                  ? 'bg-background-50/15 border-background-50/30 text-background-50 hover:bg-background-50/25'
                  : 'bg-background-100 border-background-200/70 text-foreground-800 hover:bg-background-200'
              }`}
            >
              <span>Browse Categories</span>
              {dropdownOpen ? (
                <i className="ri-arrow-up-s-line text-base"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-base"></i>
              )}
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 border border-background-200/70 rounded-2xl shadow-lg overflow-hidden py-2 max-h-96 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer group whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                      <i className={`${cat.icon} text-foreground-500 group-hover:text-primary-500 text-sm transition-colors`}></i>
                    </div>
                    <span className="flex-1">{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
              isTransparent
                ? 'bg-background-50 text-foreground-900 hover:bg-background-50/90'
                : 'bg-primary-500 text-background-50 hover:bg-primary-600'
            }`}
          >
            Pandabuy Sheet
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
            isTransparent
              ? 'bg-background-50/20 text-background-50'
              : 'bg-background-100 text-foreground-800'
          }`}
        >
          <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 max-h-[75vh] overflow-y-auto">
          <div className="flex items-center gap-3 mb-4 pb-3 border-b border-background-200/50">
            <Link
              to="/"
              onClick={closeAll}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-secondary-100 text-secondary-700 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              <span>Home</span>
            </Link>
            <Link
              to="/pandabuy-spreadsheet-guide"
              onClick={closeAll}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-accent-100 text-accent-700 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-book-open-line"></i>
              <span>Shopping Guide</span>
            </Link>
          </div>

          <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">Categories</p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 px-3 py-3 rounded-xl text-xs text-foreground-700 bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer"
                onClick={closeAll}
              >
                <div className="w-7 h-7 rounded-lg bg-background-200 flex items-center justify-center shrink-0">
                  <i className={`${cat.icon} text-foreground-500 text-xs`}></i>
                </div>
                <span className="leading-tight">{cat.name}</span>
              </a>
            ))}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="block w-full text-center px-6 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap"
          >
            Pandabuy Sheet
          </a>
        </div>
      )}
    </nav>
  );
}